import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  Stack,
  Typography,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { ENDPOINT, IMAGE_BASE_URL } from "../../../endpoints/endpoints";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const LiveDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [live, setLive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleteDialog, setDeleteDialog] = useState(false);

  // Token check
  useEffect(() => {
    if (!localStorage.getItem("accessToken")) {
      toast.warning("Please log in to continue");
      navigate("/login");
    }
  }, [navigate]);

  // Fetch Live Detail
  useEffect(() => {
    const fetchDetail = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem("accessToken");

        const res = await axios.get(ENDPOINT.LIVE_DETAIL(id), {
          headers: { Authorization: `Bearer ${token}` },
        });

        // 🔥 FIXED: your backend uses "result" NOT "data"
        setLive(res.data.result || res.data.data);
      } catch (err) {
        toast.error("Failed to fetch live detail");
      } finally {
        setLoading(false);
      }
    };

    fetchDetail();
  }, [id]);

  // Delete handler
  const handleDelete = async () => {
    try {
      const token = localStorage.getItem("accessToken");

      const res = await axios.delete(ENDPOINT.LIVE_DELETE(id), {
        headers: { Authorization: `Bearer ${token}` },
      });

      toast.success("Live deleted successfully!");

      setTimeout(() => navigate("/live/list"), 1200);
    } catch (err) {
      toast.error("Failed to delete live");
    } finally {
      setDeleteDialog(false);
    }
  };

  // Loading
  if (loading) {
    return (
      <Box display="flex" height="80vh" justifyContent="center" alignItems="center">
        <CircularProgress />
      </Box>
    );
  }

  // Not found
  if (!live) {
    return (
      <Container maxWidth="md" sx={{ mt: 5 }}>
        <Typography variant="h6" align="center" color="text.secondary">
          Live not found.
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 5, mb: 5 }}>
      <ToastContainer />

      <Paper elevation={3} sx={{ p: 4, borderRadius: 3 }}>
        {/* Header */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h5" fontWeight={600}>
            🖼 Live Detail
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate("/live/list")}
            sx={{ textTransform: "none" }}
          >
            ← Back to List
          </Button>
        </Stack>

        <Divider sx={{ mb: 3 }} />

        {/* Details */}
        <Stack spacing={2}>
          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              Live Category
            </Typography>
            <Typography variant="body1">{live.category.name}</Typography>
          </Box>

          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              League
            </Typography>
            <Typography variant="body1"><Box
                                  component="img"
                                  src={`${IMAGE_BASE_URL}${live.league.image}`} 
                                  alt={live.league.name}
                                  sx={{
                                    width: 28,
                                    height: 28,
                                    borderRadius: "50%",
                                    objectFit: "cover",
                                    mr: 1.5,
                                  }}
                                />{live.league.name}</Typography>
          </Box>

           <Box>
            <Typography variant="subtitle2" color="text.secondary">
              Team1
            </Typography>
            <Typography variant="body1"><Box
                                  component="img"
                                  src={`${IMAGE_BASE_URL}${live.team1.image}`} 
                                  alt={live.team1.name}
                                  sx={{
                                    width: 28,
                                    height: 28,
                                    borderRadius: "50%",
                                    objectFit: "cover",
                                    mr: 1.5,
                                  }}
                                />{live.team1.name}</Typography>
          </Box>

          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              Team2
            </Typography>
            <Typography variant="body1"><Box
                                  component="img"
                                  src={`${IMAGE_BASE_URL}${live.team2.image}`} 
                                  alt={live.team2.name}
                                  sx={{
                                    width: 28,
                                    height: 28,
                                    borderRadius: "50%",
                                    objectFit: "cover",
                                    mr: 1.5,
                                  }}
                                />{live.team2.name}</Typography>
          </Box>

           <Box>
            <Typography variant="subtitle2" color="text.secondary">
              Start Date
            </Typography>
            <Typography variant="body1">{live.startdate}</Typography>
          </Box>

           <Box>
            <Typography variant="subtitle2" color="text.secondary">
              Start Time
            </Typography>
            <Typography variant="body1">{live.starttime}</Typography>
          </Box>

          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              SD 1
            </Typography>
            <Typography variant="body1">{live.sd_link1}</Typography>
          </Box>

          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              SD 2
            </Typography>
            <Typography variant="body1">{live.sd_link2}</Typography>
          </Box>

          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              SD 3
            </Typography>
            <Typography variant="body1">{live.sd_link3}</Typography>
          </Box>

           <Box>
            <Typography variant="subtitle2" color="text.secondary">
              HD 1
            </Typography>
            <Typography variant="body1">{live.hd_link1}</Typography>
          </Box>

          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              HD 2
            </Typography>
            <Typography variant="body1">{live.hd_link2}</Typography>
          </Box>

          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              HD 3
            </Typography>
            <Typography variant="body1">{live.hd_link3}</Typography>
          </Box>
        </Stack>

        <Divider sx={{ my: 3 }} />

        {/* Action Buttons */}
        <Stack direction="row" justifyContent="flex-end" spacing={2}>
          <Button
            variant="outlined"
            color="warning"
            onClick={() => navigate(`/live/update/${live.id}`)}
            sx={{ textTransform: "none" }}
          >
            Edit Live
          </Button>

          <Button
            variant="contained"
            color="error"
            sx={{ textTransform: "none" }}
            onClick={() => setDeleteDialog(true)}
          >
            Delete
          </Button>
        </Stack>
      </Paper>

      {/* Delete Dialog */}
      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this live? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default LiveDetail;
